// @ts-strict-ignore
import { getProductTableWithRelations } from '../../utils/contentful'
import fetchData from './fetch-data'
import { getESData } from './elasticsearch-selectors'
import { FsCore } from '../../types/fs-core'
import { setCalculator } from '../../redux/calculators/calculators-actions'
import { setPageOffset, updateProductTable } from '../../redux/product-tables/product-table-actions'
import { setFilterGroup } from '../../redux/filters/filters-actions'
import {
  getNoEligibilityResults,
  tableState as getTableState,
} from '../../redux/product-tables/product-table-selectors'
import { query } from '../../redux/app-selectors'

export const SET_DATA = 'SET_DATA'
export const SET_BASE_SORTS = 'SET_BASE_SORTS'
export const SET_SELECTED_SORT = 'SET_SELECTED_SORT'
export const SET_FILTERS = 'SET_FILTERS'
export const SET_CALCULATOR = 'SET_CALCULATOR'
export const SET_PRODUCT_TABLE = 'SET_PRODUCT_TABLE'
export const SET_PAGINATION_DATA = 'SET_PAGINATION_DATA'
export const SET_PAGE_OFFSET = 'SET_PAGE_OFFSET'
export const SET_MAX_PER_PAGE = 'SET_MAX_PER_PAGE'
export const SET_IS_LOADING = 'SET_IS_LOADING'
export const SET_QUERY_LOADING = 'SET_QUERY_LOADING'
export const SET_IS_ERRORED = 'SET_IS_ERRORED'
export const SET_QUERY_ERRORED = 'SET_QUERY_ERRORED'
export const SET_QUERY = 'SET_QUERY'
export const SET_PRODUCT = 'SET_PRODUCT'
export const SET_QUERY_RESULT = 'SET_QUERY_RESULT'
export const SET_DATA_BY_ID = 'SET_DATA_BY_ID'
export const SET_VISUAL_FILTER_BLOCK_ID = 'SET_VISUAL_FILTER_BLOCK_ID'
export const SET_PRODUCT_DATA_SET_RESULT = 'SET_PRODUCT_DATA_SET_RESULT'

export interface SetQueryResultPayload {
  queryId: string
  calculatorId?: string
  isLoading?: boolean
  isErrored?: boolean
  data: FsCore.ESQueryData
}

export const setQueryResult = (payload: SetQueryResultPayload) => ({
  type: SET_QUERY_RESULT,
  payload,
})

export const setVisualFilterBlockId = (payload: any) => ({
  type: SET_VISUAL_FILTER_BLOCK_ID,
  payload,
})

export const setIsLoading = (payload: boolean) => (dispatch: any) =>
  dispatch({ type: SET_IS_LOADING, payload })

export interface SetQueryLoadingPayload {
  isLoading: boolean
  queryId: string
}
export const setQueryLoading = (payload: SetQueryLoadingPayload) => (dispatch: any) =>
  dispatch({ type: SET_QUERY_LOADING, payload })
export interface SetQueryErroredPayload {
  isErrored: boolean
  queryId: string
}
export const setQueryErrored = (payload: SetQueryErroredPayload) => ({
  type: SET_QUERY_ERRORED,
  payload,
})
export const setIsErrored = (payload: boolean) => (dispatch: any) =>
  dispatch({ type: SET_IS_ERRORED, payload })

export const setProduct = (payload: any) => ({ type: SET_PRODUCT, payload })

export const setProductDataSetResult = (payload: any) => ({
  type: SET_PRODUCT_DATA_SET_RESULT,
  payload,
})

export interface GetDataOptions {
  newSort?: FsCore.SortState
  newFilters?: FsCore.FilterGroupState
  newCalculator?: FsCore.CalculatorState
  pageOffset?: number
  productTableId: string
  overrideQueryId?: string
  maxPerPage?: number
  loadingSpinner?: boolean
}

export const getData = ({
  newSort,
  newFilters,
  newCalculator,
  pageOffset = 0,
  productTableId,
  overrideQueryId,
  maxPerPage,
  loadingSpinner = true,
}: GetDataOptions) => {
  return async (dispatch: any, getState: () => FsCore.State) => {
    const state = await getState()
    const { productDataset, productTableSchema } = getProductTableWithRelations(
      state,
      productTableId!,
    )

    const noEligibilityResults = getNoEligibilityResults(state, productDataset!.sys.id)

    const { eligibilityResults } = getTableState(state, productDataset!.sys.id)

    const calculatorId = productTableSchema!.fields.schema.calculatorId
    const filtersId = productTableSchema!.fields.schema.filtersId

    if (loadingSpinner) {
      dispatch(setQueryLoading({ isLoading: true, queryId: productDataset!.sys.id }))
    }

    const queryParams = query(state)

    const scoredEligibilityIds = (eligibilityResults?.results || [])
      .map((r: FsCore.EligibilityResult) => {
        // If user has selected specific product then increase the score by 110 so that it is shown at the top of results
        if (queryParams.product_id && queryParams.product_id === r.productId) {
          return { id: r.productId, score: (r.likelihood ?? 0) + 110 }
        }
        return { id: r.productId, score: r.likelihood ?? 0 }
      })
      .filter((i) => i.id)

    const data = await fetchData({
      state,
      newSort,
      calculatorId,
      filtersId,
      newFilters,
      newCalculator,
      pageOffset,
      productTableId,
      overrideQueryId,
      maxPerPage,
      scoredEligibilityIds,
      noEligibilityResults,
    })
    const appliedHits = data.hits.hits.map((hit: any) => {
      const { _id } = hit
      const eligibility = (eligibilityResults?.results || []).find((result: any) => {
        return _id === result.productId
      })

      return {
        ...hit,
        eligibility,
      }
    })

    let appendedData = data
    appendedData.hits.hits = appliedHits

    // if we are paginating append the data
    if (pageOffset > 0) {
      const previousData = getESData(state, productDataset!.sys.id) || { hits: { hits: [] } }
      appendedData = {
        ...previousData,
        hits: {
          ...previousData.hits,
          hits: [...previousData.hits.hits, ...appliedHits],
        },
      }
    }

    appendedData.hits.total = appendedData.hits.total.value || appendedData.hits.total

    if (newSort && productTableId) {
      dispatch(
        updateProductTable({
          id: productDataset!.sys.id,
          pageOffset,
          selectedSort: newSort,
        }),
      )
    } else if (productTableId) {
      dispatch(
        setPageOffset({
          id: productDataset!.sys.id,
          pageOffset,
        }),
      )
    }
    if (calculatorId) {
      dispatch(
        setQueryResult({
          queryId: productDataset!.sys.id,
          calculatorId,
          data: appendedData,
        }),
      )
    }
    dispatch(setQueryLoading({ isLoading: false, queryId: productDataset!.sys.id }))
    if (newFilters && filtersId) {
      dispatch(
        setFilterGroup({
          id: filtersId,
          filterGroup: newFilters,
        }),
      )
    }
    if (newCalculator && calculatorId) {
      newCalculator &&
        dispatch(
          setCalculator({
            id: calculatorId,
            calculator: newCalculator,
          }),
        )
    }
  }
}

export type GetDataType = typeof getData
